'use client';
/**
 * LE CENTRE DES NOTIFICATIONS (06/10/2026).
 *
 * ── Ce que cet écran répare ────────────────────────────────────────────────
 *
 * « Je veux un mail quand il y a une demande de rendez-vous ou un avis
 *   d'usager. »  Les deux partaient déjà. Aucun des deux n'arrivait :
 *
 *   · les adresses par défaut étaient FICTIVES (`rdv@bio24.labo.tn`) et rien
 *     n'amorçait de vraie valeur en base ;
 *   · le seul écran qui les changeait n'était atteignable que par l'URL
 *     `?params=1`, sans aucune entrée de menu ;
 *   · l'envoi était `fire-and-forget` : un refus du serveur de courriel ne
 *     vivait que dans le journal du serveur.
 *
 * ⚠️ LE DÉFAUT N'ÉTAIT PAS « LE COURRIEL NE MARCHE PAS ». Il était qu'on ne
 * pouvait pas le SAVOIR. C'est pourquoi cet écran existe en plus du réglage :
 * paramétrer un mécanisme dont on ne peut pas constater l'effet, c'est régler à
 * l'aveugle — et c'est exactement ce que le laboratoire a fait pendant des mois.
 *
 * ── Il LIT, il n'écrit pas le réglage ──────────────────────────────────────
 *
 * ⚠️ Les règles se modifient au Paramétrage → Alertes → Notifications, et nulle
 * part ailleurs. Le bouton « Régler » ouvre cet onglet. Un second formulaire ici
 * aurait créé deux portes vers le même objet, dont une que personne ne
 * surveillerait : c'est précisément l'histoire de `PersonnalisationTab`.
 *
 * Ce que cet écran apporte, et que l'onglet ne peut pas porter : le REGISTRE
 * DES ENVOIS. Il grossit, il se filtre, il s'adresse par URL.
 *
 * ⚠️ UN REGISTRE VIDE EST UNE AFFIRMATION. « Aucun envoi » servi avant
 * l'arrivée des données se lirait « rien n'a jamais été notifié », c'est-à-dire
 * le diagnostic même qu'on vient chercher ici. L'écran attend donc sa réponse
 * avant d'affirmer quoi que ce soit (règle `faux-vides-chargement`).
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { TitrePage, card, I, Skeleton } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { fetchEcriture } from '@/lib/ecrire';
import { fmtDateHeure } from '@/lib/meta';
import { Tableau } from '@/shared/Tableau';

interface Condition { code: string; label: string; consequence: string }
interface Evenement {
  code: string; label: string; declencheur: string; vue: string;
  slug: string | null; tacheAppli: string | null;
  conditions: Condition[]; rolesDefaut: string[];
}
interface Regle {
  evenement: string; actif: boolean; courriel: boolean;
  adresses: string[]; roles: string[]; condition: string;
}
interface Envoi {
  documentId: string; ts: string; evenement: string; canal: string;
  destinataire: string; objet: string; ref: string;
  verdict: string; motif: string | null; essai: boolean;
}
interface EtatCourriel {
  configure: boolean; hote: string | null; port: number | null;
  expediteur: string | null; manques: string[]; consequence: string;
}
interface Porteur { nom: string; roles: string[]; joignable: boolean }

interface Etat {
  /** Pourquoi le registre manque. `null` = il a bien été lu.
   *  ⚠️ Distinct d'un registre VIDE : « indisponible » et « rien n'est jamais
   *  parti » sont deux faits opposés, et c'est le second qu'on vient vérifier
   *  ici. Les confondre donnerait le diagnostic inverse de la réalité. */
  registreIndisponible: string | null;
  evenements: Evenement[];
  regles: Regle[];
  porteurs: Porteur[];
  courriel: EtatCourriel;
  envois: Envoi[];
  envoisTronques: boolean;
  envoisTotal: number;
}

/* Les libellés et les tons des verdicts. ⚠️ UN SEUL verdict est vert, et
   `echec` est rouge : c'est tout l'objet du registre. Les cinq autres sont
   AMBRE et non gris — « règle désactivée » ou « aucun destinataire » ne sont pas
   des états neutres, ce sont des raisons pour lesquelles personne n'a été
   prévenu. Les peindre en gris les aurait fait lire comme du bruit. */
const VERDICTS: Record<string, { label: string; bg: string; fg: string }> = {
  envoye: { label: 'Envoyé', bg: 'var(--green-50)', fg: 'var(--green-700)' },
  echec: { label: 'Échec', bg: 'var(--red-50)', fg: 'var(--red-600)' },
  sans_destinataire: { label: 'Aucun destinataire', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  canal_ferme: { label: 'Courriel décoché', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  regle_inactive: { label: 'Règle désactivée', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  condition: { label: 'Écarté par la condition', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  non_configure: { label: 'Aucun serveur d’envoi', bg: 'var(--red-50)', fg: 'var(--red-600)' },
};

/** Un horodatage lisible ; un `ts` illisible est rendu tel quel plutôt qu'en « Invalid Date ». */
const horodatage = (iso: string): string => {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso || '—';
  return fmtDateHeure(d);
};

/**
 * La phrase qui dit la CONSÉQUENCE d'une règle.
 *
 * ⚠️ DOUBLE de `backend/src/notifications.ts › resumeRegle`, et c'est assumé :
 * le moteur la calcule pour ses propres besoins (journal, essai), l'écran la
 * calcule pour l'afficher à côté des cases. Elles peuvent diverger sans
 * conséquence fonctionnelle — ce sont deux phrases, pas deux décisions. La
 * décision, elle, n'est prise qu'une fois, dans le module pur.
 *
 * Ce qu'elle ne fait jamais : annoncer « 2 destinataires » sans avoir compté.
 * « actif, courriel coché » ne dit pas si quelqu'un sera prévenu.
 */
function consequence(r: Regle, e: Evenement, porteurs: Porteur[]): { phrase: string; alerte: boolean } {
  const cloche = e.tacheAppli
    ? `La cloche signale « ${e.tacheAppli} ».`
    : '⚠️ Aucune tâche de cloche ne porte ce fait : le courriel en est le SEUL signal.';

  if (!r.actif) return { phrase: `Règle désactivée — aucun courriel ne partira. ${cloche}`, alerte: !e.tacheAppli };
  if (!r.courriel) return { phrase: `Canal courriel décoché — aucun courriel ne partira. ${cloche}`, alerte: !e.tacheAppli };

  const parRole = porteurs.filter((p) => p.roles.some((x) => r.roles.includes(x)));
  const joignables = new Set<string>(r.adresses);
  const muets = parRole.filter((p) => !p.joignable).map((p) => p.nom);
  const nParRole = parRole.filter((p) => p.joignable).length;
  const n = joignables.size + nParRole;

  if (!n) {
    const motif = !r.adresses.length && !r.roles.length
      ? 'aucun destinataire n’est réglé pour cet événement'
      : muets.length
        ? `les porteurs du rôle n’ont pas d’adresse de compte (${muets.join(', ')})`
        : 'aucune personne ne porte les rôles réglés';
    return { phrase: `Personne ne sera prévenu par courriel : ${motif}. ${cloche}`, alerte: true };
  }

  const cond = r.condition === 'toutes'
    ? ''
    : ` — ${(e.conditions.find((c) => c.code === r.condition)?.label || r.condition).toLowerCase()}`;
  const manque = muets.length ? ` ⚠️ Sans adresse de compte, donc non prévenu : ${muets.join(', ')}.` : '';
  return {
    phrase: `${n} destinataire${n > 1 ? 's' : ''} prévenu${n > 1 ? 's' : ''} par courriel${cond}. ${cloche}${manque}`,
    alerte: !!muets.length,
  };
}

export function Notifications() {
  const { openSettings, session } = useGed();
  const [etat, setEtat] = React.useState<Etat | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [filtre, setFiltre] = React.useState<'tous' | 'muets' | string>('tous');
  const [essaiEnCours, setEssaiEnCours] = React.useState('');
  const [verdictEssai, setVerdictEssai] = React.useState<{ code: string; texte: string; bon: boolean } | null>(null);

  const charger = React.useCallback(async () => {
    setErreur('');
    try {
      const r = await fetch('/api/ged/ged/notifications', { credentials: 'include' });
      const j = await r.json().catch(() => null);
      if (!r.ok) { setErreur(j?.error?.message || 'Le centre des notifications n’a pas pu être lu.'); return; }
      setEtat(j as Etat);
    } catch { setErreur('Le service est injoignable. Rien n’est perdu : réessayez.'); }
  }, []);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  React.useEffect(() => { void charger(); }, [charger]);

  /** Un envoi d'ESSAI, par le chemin réel. ⚠️ Il emprunte les mêmes règles et
   *  les mêmes destinataires qu'un vrai dépôt : un essai qui aurait pris un
   *  raccourci n'aurait prouvé que le raccourci. */
  const essayer = React.useCallback(async (code: string) => {
    setEssaiEnCours(code);
    setVerdictEssai(null);
    try {
      /* ⚠️ `fetchEcriture` et non `fetch` (U2) : l'essai ÉCRIT — il envoie un
         courriel et ajoute une ligne au registre. Sans le témoin d'attente,
         rien ne bougerait entre le clic et la réponse du serveur SMTP, qui peut
         prendre une dizaine de secondes : on recliquerait, et le laboratoire
         recevrait deux essais. */
      const r = await fetchEcriture('/api/ged/ged/notifications/essai', {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ evenement: code }),
      });
      const j = await r.json().catch(() => null);
      const bon = !!j && j.verdict === 'envoye';
      setVerdictEssai({
        code,
        texte: bon
          ? `Parti vers ${j.remis} adresse${j.remis > 1 ? 's' : ''} : ${(j.destinataires || []).join(', ')}. Vérifiez la boîte — c’est la seule preuve qui compte.`
          : `Rien n’est parti — ${(VERDICTS[j?.verdict]?.label || 'échec').toLowerCase()}${j?.motif ? ` : ${j.motif}` : ''}.`,
        bon,
      });
      /* Le registre vient de gagner une ligne : on le relit, sinon l'écran
         montrerait un verdict d'essai sans la trace qui le prouve. */
      await charger();
    } catch {
      setVerdictEssai({ code, texte: 'Le service est injoignable : l’essai n’a pas été joué.', bon: false });
    } finally { setEssaiEnCours(''); }
  }, [charger]);

  const envoisVus = React.useMemo(() => {
    if (!etat) return [];
    if (filtre === 'tous') return etat.envois;
    if (filtre === 'muets') return etat.envois.filter((x) => x.verdict !== 'envoye');
    return etat.envois.filter((x) => x.evenement === filtre);
  }, [etat, filtre]);

  const muets = etat ? etat.envois.filter((x) => x.verdict !== 'envoye').length : 0;

  return (
    <section>
      <TitrePage eyebrow="Pilotage & revue" titre="Notifications & envois"
        sousTitre={<>
          Qui est prévenu quand quelque chose arrive du site, et ce qui est <strong>réellement</strong>{' '}
          parti. Les règles se modifient au paramétrage : cette page les lit, à côté de leur effet
          constaté.
        </>}
        actions={<>
          <Bouton taille="sm" type="button" onClick={() => void charger()}>
            <I n="refresh" s={13} /> Relire
          </Bouton>
          <Bouton taille="sm" variante="primaire" type="button" onClick={() => openSettings('notif')}>
            <I n="gear" s={13} /> Régler les notifications
          </Bouton>
        </>} />

      {erreur && (
        <div role="alert" style={{ ...card, padding: 14, marginTop: 14, borderInlineStart: '3px solid var(--red-600)' }}>
          <strong style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</strong>
        </div>
      )}

      {/* ⚠️ RIEN N'EST AFFIRMÉ TANT QU'ON N'A PAS LU : un « aucun envoi » servi
          pendant le chargement est le pire message de cette page. */}
      {!etat && !erreur && <div style={{ marginTop: 16 }}><Skeleton rows={6} height={44} /></div>}

      {etat && (
        <>
          {/* ── L'ÉTAT DU SERVEUR D'ENVOI, EN PREMIER ───────────────────────
              Sans serveur renseigné, aucune règle ne peut tenir sa promesse :
              c'est le fait qui commande tous les autres, il se lit d'abord.
              ⚠️ Il dit « configuré », pas « ça marche » — un hôte renseigné mais
              injoignable passe ce contrôle, et la phrase du moteur le dit. */}
          <div style={{
            ...card, padding: 14, marginTop: 16,
            borderInlineStart: `3px solid ${etat.courriel.configure ? 'var(--color-primary)' : 'var(--red-600)'}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <I n={etat.courriel.configure ? 'mail' : 'alert'} s={14} />
              <strong style={{ fontSize: 12.5, color: etat.courriel.configure ? 'var(--text-strong)' : 'var(--red-600)' }}>
                {etat.courriel.configure ? 'Serveur d’envoi renseigné' : 'Aucun serveur d’envoi renseigné'}
              </strong>
              {etat.courriel.configure && etat.courriel.hote && (
                <span dir="ltr" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {etat.courriel.hote}:{etat.courriel.port} · {etat.courriel.expediteur}
                </span>
              )}
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.55, maxWidth: '88ch' }}>
              {etat.courriel.consequence}
            </p>
            {!!etat.courriel.manques.length && (
              <ul style={{ fontSize: 12.5, color: 'var(--red-600)', margin: '8px 0 0', paddingInlineStart: 18, lineHeight: 1.6 }}>
                {etat.courriel.manques.map((m) => <li key={m}>{m}</li>)}
              </ul>
            )}
          </div>

          {/* ── LES RÈGLES, EN LECTURE ──────────────────────────────────────── */}
          <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', margin: '22px 0 10px' }}>
            Les règles — ce qui est réglé aujourd’hui
          </h3>

          {etat.evenements.map((e) => {
            const r = etat.regles.find((x) => x.evenement === e.code);
            if (!r) return null;
            const c = consequence(r, e, etat.porteurs);
            const parRole = etat.porteurs.filter((p) => p.roles.some((x) => r.roles.includes(x)));
            const essai = verdictEssai?.code === e.code ? verdictEssai : null;
            return (
              <article key={e.code} style={{
                ...card, padding: 15, marginBottom: 10,
                borderInlineStart: `3px solid ${c.alerte ? 'var(--amber-600)' : 'var(--border-default)'}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13, color: 'var(--navy-900)' }}>{e.label}</strong>
                  <span style={{
                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em',
                    padding: '2px 7px', borderRadius: 'var(--radius-xs)',
                    background: r.actif && r.courriel ? 'var(--green-50)' : 'var(--gray-50)',
                    color: r.actif && r.courriel ? 'var(--green-700)' : 'var(--text-muted)',
                  }}>
                    {r.actif ? (r.courriel ? 'Courriel actif' : 'Courriel décoché') : 'Désactivée'}
                  </span>
                </div>
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '5px 0 0', lineHeight: 1.55 }}>
                  {e.declencheur}
                </p>
                <p style={{
                  fontSize: 12.5, margin: '9px 0 0', lineHeight: 1.6,
                  color: c.alerte ? 'var(--amber-600)' : 'var(--text-body)', fontWeight: c.alerte ? 600 : 400,
                }}>
                  {c.phrase}
                </p>

                {(!!r.adresses.length || !!r.roles.length) && (
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.7 }}>
                    {!!r.adresses.length && <div dir="ltr">Adresses réglées : <strong>{r.adresses.join(', ')}</strong></div>}
                    {!!r.roles.length && (
                      <div>
                        Rôles réglés : <strong>{r.roles.join(', ')}</strong>
                        {' — '}
                        {parRole.length
                          ? parRole.map((p) => `${p.nom}${p.joignable ? '' : ' (sans adresse)'}`).join(', ')
                          : 'personne ne les porte'}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 11, flexWrap: 'wrap' }}>
                  <Bouton taille="sm" type="button" disabled={essaiEnCours === e.code}
                    onClick={() => void essayer(e.code)}>
                    <I n="send" s={12} /> {essaiEnCours === e.code ? 'Envoi en cours…' : 'Envoyer un essai'}
                  </Bouton>
                  <Bouton taille="sm" variante="discret" type="button" onClick={() => openSettings('notif')}>
                    Régler
                  </Bouton>
                </div>

                {essai && (
                  <p role="status" style={{
                    fontSize: 12.5, margin: '9px 0 0', lineHeight: 1.6,
                    color: essai.bon ? 'var(--green-700)' : 'var(--red-600)', fontWeight: 600,
                  }}>
                    {essai.texte}
                  </p>
                )}
              </article>
            );
          })}

          {/* ── LE REGISTRE DES ENVOIS ──────────────────────────────────────── */}
          <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', margin: '26px 0 4px' }}>
            Le registre des envois — ce qui est parti, et ce qui ne l’est pas
          </h3>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.6, maxWidth: '88ch' }}>
            Les <strong>non-envois y figurent aussi</strong>, et c’est l’essentiel : « règle
            désactivée », « aucun destinataire » ou « écarté par la condition » sont justement les
            cas où l’on cherche pourquoi on n’a rien reçu.
          </p>

          {/* ⚠️ `Segment` et non une rangée de `Bouton` : choisir une vue n'est
              pas une action, et une option active dessinée en bouton plein
              concurrencerait l'action principale de l'écran
              (`shared/docs/UI-CONVENTIONS.md` §1). Le choix est unique, ce qui
              est bien la forme de ce filtre. */}
          <div style={{ marginBottom: 12 }}>
            <Segment ariaLabel="Filtrer les envois" valeur={filtre}
              onChange={(v) => setFiltre(v)}
              options={[
                { valeur: 'tous', libelle: 'Tous', compteur: etat.envois.length },
                {
                  valeur: 'muets', libelle: 'Personne n’a été prévenu', compteur: muets,
                  aide: 'Règle désactivée, aucun destinataire, condition qui écarte, ou refus du serveur de courriel.',
                },
                ...etat.evenements.map((e) => ({
                  valeur: e.code,
                  libelle: e.label,
                  compteur: etat.envois.filter((x) => x.evenement === e.code).length,
                })),
              ]} />
          </div>

          {/* ⚠️ La TRONCATURE est dite. Un registre tronqué en silence se lit
              comme « il n'y a rien eu avant » — le piège de la pagination muette,
              déjà rencontré trois fois dans ce dépôt. */}
          {etat.envoisTronques && (
            <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '0 0 10px', fontWeight: 600 }}>
              ⚠️ Seuls les {etat.envois.length} envois les plus récents sont affichés, sur{' '}
              {etat.envoisTotal} au total.
            </p>
          )}

          {/* L7 vague 2 (11/10/2026) : registre STANDARD sur le socle — un journal
              qui ne fera que croître : paginé, exporté (export de référence),
              filtres par événement et par issue. « Registre indisponible » est
              l'état d'ERREUR du tableau, distinct d'un registre VIDE : « indisponible »
              et « rien n'est jamais parti » sont deux faits opposés. */}
          <Tableau
            id="notifications-envois"
            ajusterLargeur
            niveau="standard"
            parPage={50}
            quoi="envois"
            erreur={etat.registreIndisponible ? `Registre indisponible — ${etat.registreIndisponible}` : null}
            onReessayer={() => void charger()}
            vide={{
              titre: filtre === 'tous' ? 'Aucun envoi enregistré' : 'Aucun envoi pour ce filtre',
              indice: filtre === 'tous'
                ? 'Le registre a commencé le jour de sa mise en service : il ne porte rien d’avant. Envoyez un essai ci-dessus pour vérifier qu’une règle aboutit.'
                : 'Retirez le filtre pour voir les autres.',
            }}
            lignes={envoisVus}
            cleLigne={(x) => x.documentId}
            export={{
              registre: 'notifications-envois', libelleRegistre: 'Registre des envois', auteur: session.name,
              filtres: filtre === 'tous' ? [] : [filtre === 'muets' ? 'Personne n’a été prévenu' : `Événement « ${etat.evenements.find((e) => e.code === filtre)?.label || filtre} »`],
            }}
            colonnes={[
              {
                cle: 'quand', libelle: 'Quand', type: 'date', valeur: (x) => x.ts, export: true, exporter: (x) => horodatage(x.ts),
                style: { whiteSpace: 'nowrap', color: 'var(--text-muted)' },
                rendu: (x) => (Number.isFinite(new Date(x.ts).getTime()) ? fmtDateHeure(new Date(x.ts)) : x.ts || '—'),
              },
              {
                cle: 'evenement', libelle: 'Événement', type: 'liste', valeur: (x) => x.evenement, export: true,
                libelleValeur: (code) => etat.evenements.find((y) => y.code === code)?.label || code,
                exporter: (x) => `${etat.evenements.find((y) => y.code === x.evenement)?.label || x.evenement}${x.essai ? ' (essai)' : ''}`,
                rendu: (x) => (
                  <>
                    {etat.evenements.find((y) => y.code === x.evenement)?.label || x.evenement}
                    {x.essai && (
                      <span style={{
                        marginInlineStart: 6, fontSize: 11, fontWeight: 700, padding: '1px 6px',
                        borderRadius: 'var(--radius-xs)', background: 'var(--gray-50)', color: 'var(--text-muted)',
                      }}>essai</span>
                    )}
                  </>
                ),
              },
              { cle: 'ref', libelle: 'Pièce', valeur: (x) => x.ref || '', export: true, style: { color: 'var(--text-muted)' }, rendu: (x) => x.ref || '—' },
              { cle: 'destinataire', libelle: 'Destinataire', valeur: (x) => x.destinataire || '', export: true, style: { color: 'var(--text-muted)' }, rendu: (x) => <span dir="ltr">{x.destinataire || '—'}</span> },
              {
                cle: 'verdict', libelle: 'Issue', type: 'liste', valeur: (x) => x.verdict, export: true,
                libelleValeur: (v) => VERDICTS[v]?.label || v,
                exporter: (x) => `${VERDICTS[x.verdict]?.label || x.verdict}${x.motif ? ` — ${x.motif}` : ''}`,
                rendu: (x) => {
                  const v = VERDICTS[x.verdict] || { label: x.verdict, bg: 'var(--gray-50)', fg: 'var(--text-muted)' };
                  return (
                    <>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-xs)',
                        background: v.bg, color: v.fg, whiteSpace: 'nowrap',
                      }}>{v.label}</span>
                      {x.motif && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.5 }}>
                          {x.motif}
                        </div>
                      )}
                    </>
                  );
                },
              },
            ]}
          />
        </>
      )}
    </section>
  );
}
